import React from 'react';
import PropTypes from 'prop-types';
import { MdAdd, MdOutlineRoute } from 'react-icons/md';
import Button from './Button';
import Icon from '../Icons/Icon';
import { Plus } from '../Icons/Icons';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Button/Button',
  component: Button,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Button` component provides users with an interactive UI element for triggering actions or navigating to different parts of the app. It supports multiple styles, sizes, and icon integrations for versatility in design.\n\n',
      // When & how to use it?
      `\nUse the \`Button\` component whenever user interaction is required. This includes:
      \n- Form submissions
      \n- Navigation links
      \n- Action triggers like adding or deleting items
      \n\nIt is flexible and customizable to match different UI and UX requirements.\n\n`,
      // Current Usage
      `\nThe \`Button\` component is used across the application, including:
      \n- Action buttons on modals
      \n- Navigation elements on forms
      \n- Add/Edit/Delete buttons for data interactions
      \n\nIt ensures consistency and accessibility in user actions across the app.\n\n`,
      // Related Components
      `\n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Used to add icons to the button.
      \n- **[EmptyState](http://localhost:6006/?path=/docs/components-emptystate--docs)**: Incorporates the \`Button\` component for its action button.
      \n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    iconElement: {
      control: { type: 'select' },
      options: [null, MdAdd, MdOutlineRoute],
    },
    size: {
      control: { type: 'select' },
      options: ['rounded-md', 'sm', 'md', 'lg'],
    },
    color: {
      control: { type: 'select' },
      options: ['primary', 'secondary', 'action', 'primary-delete', 'secondary-delete'],
    },
  },
};

const ButtonTemplate = ({
  label = '',
  onClick = () => { },
  type = 'button',
  className = '',
  size = 'lg',
  iconElement = null, // Icon prop in the Button component
  color = 'primary',
}) => {
  return (<Button label={label} onClick={onClick} type={type} className={className} size={size} color={color} Icon={iconElement} />);
};

const Template = ButtonTemplate.bind({});

export const DefaultVariant = Template.bind({});
DefaultVariant.args = {
  label: 'Button',
};
DefaultVariant.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `DefaultVariant` is used as the base button style for general interactions where no specific style or icon is required.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is commonly used for general actions across the app, such as form submissions and navigation.\n\n',
);

export const SecondaryButton = Template.bind({});
SecondaryButton.args = {
  label: 'Secondary Button',
  color: 'secondary',
};
SecondaryButton.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `SecondaryButton` variant is used when an alternate style is needed to differentiate actions from primary ones.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used for less prominent actions on forms or modals where secondary importance is needed.\n\n',
);

export const NewItemVariant = Template.bind({});
NewItemVariant.args = {
  label: 'Add Button',
  // sending instantiated Icon component we can handle size and className (need to add this className to add ability to change icon color if it is primary button)
  iconElement: <MdAdd size={24} className="text-vamos-green1 group-hover:text-vamos-black" />,
};
NewItemVariant.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `NewItemVariant` is used for adding new items, such as creating new records or adding to lists.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is widely used for "Add" buttons across modals, tables, and pages like `EmptyState`.\n\n',
);

export const CustomIconVariant = Template.bind({});
CustomIconVariant.args = {
  label: 'Custom Icon',
  iconElement: MdOutlineRoute, // sending not instantiated Icon component <Button Icon={MdOutlineRoute}/>
};
CustomIconVariant.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `CustomIconVariant` is used when a button requires a specific custom icon to enhance its visual appeal and functionality.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used on forms and modals for actions requiring custom icons, such as routing or navigation.\n\n',
);

export const Login = Template.bind({});
Login.args = {
  label: 'Login',
};
Login.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `Login` variant is used for authentication-related actions like logging in or signing up.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is prominently used on the login page for user authentication.\n\n',
);

export const ActionVariant = Template.bind({});
ActionVariant.args = {
  label: '',
  iconElement: <Icon name="download" />,
  color: 'action',
};
ActionVariant.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `ActionVariant` is used for action-based buttons, such as downloading or deleting items.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used across the app for action buttons in lists, cards, and modals.\n\n',
);

export const EllipseButton = Template.bind({});
EllipseButton.args = {
  iconElement: <Plus width={11} height={11} />,
  size: 'ellipse-sm',
};

EllipseButton.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `EllipseButton` variant is used for small, circular buttons with icons.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used for adding new items in tables and lists.\n\n',
);

export const PrimaryDeleteButton = Template.bind({});
PrimaryDeleteButton.args = {
  label: 'Delete',
  color: 'primary-delete',
};
PrimaryDeleteButton.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `PrimaryDeleteButton` variant is a primary variant for `Delete` Button.\n\n',
  // Where is it currently used on the product?
  '\nThis variant currently is on the Mock-up.\n\n',
);

export const SecondaryDeleteButton = Template.bind({});
SecondaryDeleteButton.args = {
  label: 'Delete',
  color: 'secondary-delete',
};
SecondaryDeleteButton.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `SecondaryDeleteButton` variant is a primary variant for `Delete` Button.\n\n',
  // Where is it currently used on the product?
  '\nThis variant currently used in the `View Note` modal.\n\n',
);

ButtonTemplate.propTypes = {
  onClick: PropTypes.func,
  type: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['lg', 'md']),
  iconElement: PropTypes.element,
  label: PropTypes.string,
  color: PropTypes.string,
};
